/**
 * SaveManager — estado da fazenda em IndexedDB + fila de sync.
 *
 * Secao 14: localStorage e sessionStorage sao PROIBIDOS. Tudo mora no IndexedDB.
 * Secao 9: o cliente e a fonte de verdade. Nunca existe merge de estado —
 *          vence a versao maior, e o perdedor vai para o historico no servidor.
 * Falha de rede e SILENCIOSA. A crianca nunca ve aviso de sync (secao 9).
 */

import { openDB } from 'idb';

const BANCO = 'fazenda-dos-amigos';
const VERSAO_BANCO = 1;
const SCHEMA_ESTADO = 1;

const LIMITE_CADERNO = 40;
const LIMITE_COLECAO = 60;
const INTERVALO_SYNC_MS = 60_000;

export class SaveManager {
  constructor() {
    this.db = null;
    this.estado = null;
    this.mudouDesdeUltimoSync = false;
    this.temporizador = null;
  }

  async abrir() {
    this.db = await openDB(BANCO, VERSAO_BANCO, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('estado')) {
          db.createObjectStore('estado', { keyPath: 'perfil_id' });
        }
        if (!db.objectStoreNames.contains('fila')) {
          db.createObjectStore('fila', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('dispositivo')) {
          db.createObjectStore('dispositivo', { keyPath: 'chave' });
        }
      },
    });
    return this;
  }

  /** Carrega o estado do perfil, ou cria uma fazenda nova. */
  async carregar(perfilId) {
    const guardado = await this.db.get('estado', perfilId);
    this.estado = guardado ? migrar(guardado) : estadoNovo(perfilId);
    console.info(
      `FAZENDA: estado carregado — ano ${this.estado.ano}, ${this.estado.estacao}, ` +
        `dia ${this.estado.dia}, versao ${this.estado.versao}.`
    );
    return this.estado;
  }

  /**
   * Grava o estado. `versao` e contador monotonico: sobe a cada mudanca.
   * D14: cada perfil tem o seu registro; nunca ha progresso compartilhado.
   */
  async salvar() {
    if (!this.estado) {
      throw new Error('FAZENDA: tentei salvar sem estado carregado. Chame carregar() antes.');
    }

    this.estado.versao += 1;
    this.podar();

    await this.db.put('estado', this.estado);
    this.mudouDesdeUltimoSync = true;

    const bytes = new Blob([JSON.stringify(this.estado)]).size;
    if (bytes > 100_000) {
      console.warn(
        `FAZENDA: o estado esta com ${Math.round(bytes / 1024)} KB, acima do alvo de 100 KB ` +
          '(secao 8). O servidor rejeita acima de 512 KB. Reduza caderno ou colecao.'
      );
    }
    return this.estado;
  }

  /** Secao 8: podar caderno e colecao para nao crescerem sem limite. */
  podar() {
    if (this.estado.caderno.length > LIMITE_CADERNO) {
      this.estado.caderno = this.estado.caderno.slice(-LIMITE_CADERNO);
    }
    if (this.estado.colecao.length > LIMITE_COLECAO) {
      this.estado.colecao = this.estado.colecao.slice(-LIMITE_COLECAO);
    }
  }

  // -------------------------------------------------------------------------
  // Fila de sync — drena quando der. Falha de rede nunca aparece na tela.
  // -------------------------------------------------------------------------

  async enfileirarEvento(evento) {
    await this.db.add('fila', { tipo: 'evento', dados: evento, em: Date.now() });
  }

  iniciarSyncAutomatico() {
    this.temporizador = setInterval(() => {
      if (this.mudouDesdeUltimoSync) this.sincronizar();
    }, INTERVALO_SYNC_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.sincronizarComBeacon();
    });

    window.addEventListener('online', () => this.sincronizar());
  }

  pararSyncAutomatico() {
    if (this.temporizador) clearInterval(this.temporizador);
    this.temporizador = null;
  }

  /**
   * Envia estado e eventos. Silencioso por design (secao 9): se o servidor
   * estiver fora do ar, o jogo continua igual e a fila drena depois.
   * O console registra, para o adulto conseguir diagnosticar.
   */
  async sincronizar() {
    if (!this.estado) return;

    const token = await this.tokenDoDispositivo();
    if (!token) return; // ainda nao registrado; nada a fazer

    try {
      const resposta = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ perfil_id: this.estado.perfil_id, estado: this.estado }),
      });

      if (resposta.status === 409) {
        // Conflito: vence a versao maior. NUNCA merge (secao 14).
        const doServidor = await resposta.json();
        if (doServidor.estado?.versao > this.estado.versao) {
          this.estado = migrar(doServidor.estado);
          await this.db.put('estado', this.estado);
          console.info('FAZENDA: o servidor tinha uma versao maior. Estado local substituido.');
        }
        this.mudouDesdeUltimoSync = false;
        return;
      }

      if (!resposta.ok) {
        console.info(`FAZENDA: sync adiado (servidor respondeu ${resposta.status}). A fila drena depois.`);
        return;
      }

      this.mudouDesdeUltimoSync = false;
      await this.drenarFila(token);
    } catch (e) {
      // Rede fora do ar. Nada na tela da crianca — so no console do adulto.
      console.info(`FAZENDA: sem rede agora (${e.message}). O jogo segue normal; a fila drena depois.`);
    }
  }

  /** Ultimo suspiro ao fechar a aba. sendBeacon sobrevive ao unload. */
  sincronizarComBeacon() {
    if (!this.estado || !this.mudouDesdeUltimoSync) return;
    const corpo = new Blob(
      [JSON.stringify({ perfil_id: this.estado.perfil_id, estado: this.estado })],
      { type: 'application/json' }
    );
    navigator.sendBeacon?.('/api/sync', corpo);
  }

  async drenarFila(token) {
    const pendentes = await this.db.getAll('fila');
    if (pendentes.length === 0) return;

    const eventos = pendentes.filter((p) => p.tipo === 'evento').map((p) => p.dados);
    if (eventos.length === 0) return;

    const resposta = await fetch('/api/eventos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ perfil_id: this.estado.perfil_id, eventos }),
    });

    if (resposta.ok) {
      const tx = this.db.transaction('fila', 'readwrite');
      for (const p of pendentes) tx.store.delete(p.id);
      await tx.done;
    }
  }

  async tokenDoDispositivo() {
    const registro = await this.db.get('dispositivo', 'token');
    return registro?.valor ?? null;
  }

  async guardarTokenDoDispositivo(valor) {
    await this.db.put('dispositivo', { chave: 'token', valor });
  }
}

/** Estrutura da secao 8. */
function estadoNovo(perfilId) {
  return {
    schema: SCHEMA_ESTADO,
    versao: 0,
    perfil_id: perfilId,
    ano: 1,
    estacao: 'primavera',
    dia: 1,
    sessoes_na_estacao: 0,
    construcoes: {},
    canteiros: [],
    animais: [],
    bandas: {
      matematica: 1,
      logica: 1,
      ciencias: 1,
      engenharia: 1,
      leitura: 1,
      valores: 1,
    },
    caderno: [],
    colecao: [],
    cooldowns: {},
  };
}

/**
 * Migracoes rodam no cliente, na carga (secao 8).
 * Hoje so existe o schema 1; a estrutura ja fica pronta para o proximo.
 */
function migrar(estado) {
  if (estado.schema === undefined) {
    throw new Error(
      'FAZENDA: encontrei um estado salvo sem o campo "schema". ' +
        'Ele e obrigatorio desde a v1 (secao 8) e sem ele nao da para migrar com seguranca.'
    );
  }
  if (estado.schema > SCHEMA_ESTADO) {
    throw new Error(
      `FAZENDA: o estado salvo e do schema ${estado.schema}, mais novo que o schema ` +
        `${SCHEMA_ESTADO} que este codigo entende. Atualize o jogo antes de continuar.`
    );
  }
  // Campos que entraram depois do primeiro salvamento ganham valor padrao.
  estado.sessoes_na_estacao ??= 0;
  return estado;
}
